import { uploadMediaFile } from "../services/api";
import type { DealAgreementResponseDto } from "../types/types";

// Turns the drawn signature (data URL) into an image file, uploads it and returns the file URL.
export const uploadSignature = async (
  signatureDataUrl: string,
): Promise<string> => {
  const response = await fetch(signatureDataUrl);
  const signatureBlob = await response.blob();
  const signatureFile = new File([signatureBlob], "signature.png", {
    type: "image/png",
  });
  return await uploadMediaFile(signatureFile);
};

// Downloads a blob as a file and frees the object URL right away.
export const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  try {
    document.body.appendChild(anchor);
    anchor.click();
  } finally {
    anchor.remove();
    URL.revokeObjectURL(url);
  }
};

// Whether accepting the deal is allowed based on the agreement state.
export const canAcceptDeal = (
  agreement?: DealAgreementResponseDto | null,
): boolean => {
  if (!agreement) return true;
  return agreement.status === "FULLY_SIGNED";
};

// Whether the current user can sign the agreement right now.
export const canSignAgreement = (
  agreement?: DealAgreementResponseDto | null,
  role?: string,
): boolean => {
  if (role === "MANAGER") return agreement?.status === "UNSIGNED";
  if (role === "FIGHTER") return agreement?.status === "ORGANIZER_SIGNED";
  return false;
};

// i18n key describing the current signing state, or null when nothing special applies.
export const getAgreementMessageKey = (
  agreement?: DealAgreementResponseDto | null,
  role?: string,
): string | null => {
  if (!agreement) return null;

  switch (agreement.status) {
    case "DRAFT":
      return "agreementDraft";
    case "FULLY_SIGNED":
      return "agreementFullySigned";
    case "VOID":
      return "agreementVoided";
    case "UNSIGNED":
      return role === "MANAGER"
        ? "agreementManagerMustSign"
        : "agreementWaitingForManager";
    case "ORGANIZER_SIGNED":
      return role === "MANAGER"
        ? "agreementWaitingForFighterReview"
        : "agreementWaitingForFighter";
    case "FIGHTER_SIGNED":
      return "agreementFighterSigned";
  }
};
